import { STORE_ACTOR_ID, STORE_ACTOR_NAME, type AuditRecordDto } from '@deli/contracts';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { fetchAuditPage, PAGE_SIZE, useUsers, type AuditFilter } from '../api/staff.js';
import { QueryState } from '../components/QueryState.js';
import { formatDateTime } from '../format.js';
import { t } from '../i18n/uk.js';

const show = (values: Record<string, unknown> | null) =>
  values === null
    ? '—'
    : Object.entries(values)
        .map(([k, v]) => `${k}: ${JSON.stringify(v)}`)
        .join(', ');

export function AuditPage() {
  const [draft, setDraft] = useState<AuditFilter>({});
  const [filter, setFilter] = useState<AuditFilter>({});
  const users = useUsers();
  const names = new Map([
    [STORE_ACTOR_ID, STORE_ACTOR_NAME],
    ...(users.data ?? []).map((u) => [u.id, u.name] as const),
  ]);

  const audit = useInfiniteQuery({
    queryKey: ['audit', filter],
    initialPageParam: undefined as number | undefined,
    queryFn: ({ pageParam, signal }) => fetchAuditPage(filter, pageParam, signal),
    getNextPageParam: (last: AuditRecordDto[]) =>
      last.length === PAGE_SIZE ? last.at(-1)!.id : undefined,
  });

  const apply = (event: FormEvent) => {
    event.preventDefault();
    setFilter({
      entity: draft.entity?.trim() || undefined,
      entityId: draft.entityId?.trim() || undefined,
    });
  };

  return (
    <section>
      <h1>{t.audit.title}</h1>
      <form className="card inline-form" onSubmit={apply}>
        <input
          aria-label={t.audit.entity}
          placeholder={`${t.audit.entity} (user, app_setting…)`}
          value={draft.entity ?? ''}
          onChange={(e) => setDraft({ ...draft, entity: e.target.value })}
        />
        <input
          aria-label={t.audit.entityId}
          placeholder={t.audit.entityId}
          value={draft.entityId ?? ''}
          onChange={(e) => setDraft({ ...draft, entityId: e.target.value })}
        />
        <button type="submit">{t.audit.filter}</button>
      </form>
      <QueryState query={audit}>
        {(data) => {
          const rows = data.pages.flat();
          if (rows.length === 0) return <p className="muted">{t.audit.empty}</p>;
          return (
            <>
              <table className="table">
                <thead>
                  <tr>
                    <th>{t.audit.when}</th>
                    <th>{t.audit.who}</th>
                    <th>{t.audit.what}</th>
                    <th>{t.audit.before}</th>
                    <th>{t.audit.after}</th>
                    <th>{t.audit.reason}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td>{formatDateTime(r.at)}</td>
                      <td>{r.userId ? (names.get(r.userId) ?? r.userId) : t.audit.system}</td>
                      <td>
                        <code>{r.entity}</code> {r.action}
                        <div className="muted">{r.entityId}</div>
                      </td>
                      <td>{show(r.before)}</td>
                      <td>{show(r.after)}</td>
                      <td>{r.reason ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {audit.hasNextPage && (
                <button
                  type="button"
                  onClick={() => void audit.fetchNextPage()}
                  disabled={audit.isFetchingNextPage}
                >
                  {t.audit.more}
                </button>
              )}
            </>
          );
        }}
      </QueryState>
    </section>
  );
}
